import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  getClinicalAssessment,
  getClinicalPrescription,
  api,
} from "../../../api/apiService";
import {
  calculateAge,
  formatPatientName,
  formatDobDisplay,
} from "../../../utils/patientVisitUtils";
import EditIcon from "../../../assets/images/OPD-patients/EditPatient.svg";

import UserIcon from "../../../assets/images/comman/user.svg";
import { useAbhaLink } from "../../../context/AbhaLinkContext";
import NotifyAbhaButton from "./NotifyAbhaButton";
import CreateVisitModal from "../../../components/common/CreateVisitModal";
import EditPatientModal from "../../../components/Patients/EditPatientModal";
import authService from "../../../Services/authService";

const PatientDetailNew = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // Use state for immediate data if provided, then sync with API
  const initialData = location.state?.patientData || null;

  const [patientData, setPatientData] = useState(initialData);
  const [activeTab, setActiveTab] = useState("overview");
  const [loading, setLoading] = useState(!initialData);
  const [latestVitals, setLatestVitals] = useState(
    initialData?.vitals || initialData?.rawData?.vitals || null,
  );
  const [prescriptions, setPrescriptions] = useState([]);
  const [showVisitModal, setShowVisitModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  const { openAbhaLink } = useAbhaLink();

  useEffect(() => {
    // WASA-10 IDOR Defense: Validate parameter format before making requests
    if (!id || !/^[a-zA-Z0-9_-]{8,64}$/.test(id)) {
      navigate("/patients", { replace: true });
      return;
    }

    const loadAll = async () => {
      // If we have initial data, don't show full-page loader
      if (!initialData) setLoading(true);
      await fetchPatientDetails();
      setLoading(false);
    };
    loadAll();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchPatientDetails = async () => {
    try {
      const response = await api.get(`/patient/${id}`);
      const data = response.data?.data || response.data;

      // WASA-10 IDOR Defense: Cross-tenant hospital validation
      // Enforce client-side boundary check in addition to required backend authorization
      const currentUser = authService.getUser();
      const userHospitalId = currentUser?.hospitalId || currentUser?.hospital_id || currentUser?.hospital;
      const patientHospitalId = data?.hospitalId || data?.hospital_id || data?.hospital;
      if (userHospitalId && patientHospitalId && String(userHospitalId) !== String(patientHospitalId)) {
        console.warn("[WASA-10] Unauthorized cross-tenant patient access attempt prevented");
        navigate("/patients", { replace: true });
        return;
      }

      // Merge with existing data to preserve calculated fields if needed
      setPatientData((prev) => ({ ...prev, ...data }));

      const visitId =
        data.visitId || data.visits?.[0]?.visitId || data.visits?.[0]?._id;
      const patientId = data.id || data._id;

      if (visitId && patientId) {
        fetchClinicalData(visitId, patientId);
      }
    } catch (error) {
      console.error("Error fetching patient details:", error);
      if (error.response?.status === 401 || error.response?.status === 403 || error.response?.status === 404) {
        navigate("/patients", { replace: true });
      }
    }
  };

  const fetchClinicalData = async (visitId, patientId) => {
    try {
      const [vitalsRes, prescriptionRes] = await Promise.allSettled([
        getClinicalAssessment(visitId, patientId),
        getClinicalPrescription(visitId, patientId),
      ]);

      if (vitalsRes.status === "fulfilled") {
        const v =
          vitalsRes.value?.data?.vitals ||
          vitalsRes.value?.vitals ||
          vitalsRes.value?.data;
        if (v && typeof v === "object") setLatestVitals(v);
      }

      if (prescriptionRes.status === "fulfilled") {
        const m =
          prescriptionRes.value?.data?.medications ||
          prescriptionRes.value?.medications ||
          prescriptionRes.value?.data;
        if (Array.isArray(m)) setPrescriptions(m);
      }
    } catch (error) {
      console.error("Error fetching clinical data:", error);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "---";
    const date = new Date(dateString);
    return isNaN(date.getTime())
      ? "---"
      : date.toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        });
  };

  const formatTime = (dateString) => {
    if (!dateString) return "---";
    const date = new Date(dateString);
    return isNaN(date.getTime())
      ? "---"
      : date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  const handleVisitCreated = (result) => {
    // Refresh patient details to show new visit
    fetchPatientDetails();
    setShowVisitModal(false);
  };

  const handleUpdateSuccess = (updatedData) => {
    // Refresh patient details after profile update
    fetchPatientDetails();
    setShowEditModal(false);
  };

  if (loading && !patientData)
    return <div className="p-5 text-center">Loading...</div>;
  if (!patientData)
    return <div className="p-5 text-center">Patient not found.</div>;

  // Improved Display Logic
  const initials = (
    patientData.fullName ||
    patientData.name ||
    patientData.f_name ||
    "JD"
  )
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  const displayName = formatPatientName(patientData) || "Johnnathan Doe";

  const uhid = patientData.uhid || patientData.UHID || "992811";
  const isAbhaLinked = Boolean(
    patientData.isAbhaLinked === true ||
    patientData.isabhalinked === true ||
    patientData.isABHALinked === true ||
    String(patientData.isAbhaLinked).toLowerCase() === "true" ||
    String(patientData.isabhalinked).toLowerCase() === "true" ||
    String(patientData.isABHALinked).toLowerCase() === "true" ||
    (patientData.maskedAbha && String(patientData.maskedAbha).trim() && patientData.maskedAbha !== "N/A") ||
    (patientData.ABHANumber && String(patientData.ABHANumber).trim() && patientData.ABHANumber !== "N/A") ||
    (patientData.abhaNumber && String(patientData.abhaNumber).trim() && patientData.abhaNumber !== "N/A") ||
    (patientData.formattedAbhaId && String(patientData.formattedAbhaId).trim() && patientData.formattedAbhaId !== "N/A") ||
    (patientData.abhaaddress && String(patientData.abhaaddress).trim()) ||
    (patientData.abhaAddress && String(patientData.abhaAddress).trim()) ||
    (patientData.ABHAAddress && String(patientData.ABHAAddress).trim())
  );
  const abha =
    patientData.maskedAbha ||
    patientData.ABHANumber ||
    patientData.abhaNumber ||
    patientData.formattedAbhaId ||
    (isAbhaLinked ? "Linked" : "");
  const abhaAddress =
    patientData.ABHAAddress ||
    patientData.abhaAddress ||
    patientData.abhaaddress;
  const mobile = patientData.mobile || patientData.phone || "987-654-3210";
  const email = patientData.email || "j.doe@example.com";
  const dob = patientData.dob || patientData.dateOfBirth;
  const formattedDob = formatDobDisplay(dob);
  const calculatedAgeNum = calculateAge(formattedDob || dob);
  const displayAge =
    calculatedAgeNum > 0
      ? calculatedAgeNum
      : patientData.age && !isNaN(Number(patientData.age)) && Number(patientData.age) > 0
      ? Number(patientData.age)
      : "";

  return (
    <div className="patient-detail-new-container">
      {/* Header Actions Row */}
      <div className="header-actions-row">
        <div className="d-flex align-items-center">
          <button
            className="back-btn-rect"
            onClick={() => navigate("/patients-list")}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
            >
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
            Back
          </button>
          <h2 className="page-title">Patient Details</h2>
        </div>
        <div className="d-flex gap-3">
          {isAbhaLinked || abha ? (
            /* <button className="btn-outline-blue">Share to ABDM</button> */
            null
          ) : (
            <>
              <button
                className="btn-outline-blue"
                onClick={() => openAbhaLink(patientData)}
              >
                Link ABHA
              </button>
              <NotifyAbhaButton patientId={id} />
            </>
          )}
          {/* <button className="btn-dropdown-grey">
            Print Records
            <svg
              width="10"
              height="6"
              viewBox="0 0 10 6"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            >
              <path d="M1 1L5 5L9 1" />
            </svg>
          </button> */}
          <button
            className="btn-solid-blue"
            onClick={() => setShowVisitModal(true)}
          >
            + Book Appointment
          </button>
        </div>
      </div>

      {/* Patient Banner Card */}
      <div className="patient-banner-card">
        <div className="d-flex align-items-center gap-4">
          <div className="jd-avatar">{initials}</div>
          <div>
            <div className="d-flex align-items-center">
              <h1 className="patient-name mb-0">{displayName}</h1>
              <span className="badge-inpatient-tag">In-Patient</span>
            </div>
            <div className="demographics-row">
              <span>
                <span className="label">UHID:</span>{" "}
                <span className="value">{uhid}</span>
              </span>
              <span>
                <span className="label">ABHA:</span>{" "}
                <span className="value me-2">{abha || "Not Linked"}</span>
                {abha && abhaAddress && (
                  <>
                    · <span className="label text-muted"> ABHA ADDRESS:</span>
                    <span className="ms-2 value">{abhaAddress}</span>
                  </>
                )}
              </span>
              <span>
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  className="me-1 opacity-50"
                >
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                </svg>
                <span className="value">{mobile}</span>
              </span>
              <span>
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  className="me-1 opacity-50"
                >
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                  <polyline points="22,6 12,13 2,6" />
                </svg>
                <span className="value">{email}</span>
              </span>
            </div>
          </div>
        </div>
        <div className="d-flex gap-3">
          <button className="edit-btn" onClick={() => setShowEditModal(true)}>
            <img src={EditIcon} width="16" alt="edit" /> Edit
          </button>

          {/* <button className="delete-btn">
            <img src={DeleteIcon} width="16" alt="delete" />
          </button> */}
        </div>
      </div>

      {/* Tabs */}
      <div className="tabs-nav-bar">
        <div
          className={`tab-item ${activeTab === "overview" ? "active" : ""}`}
          onClick={() => setActiveTab("overview")}
        >
          Overview
        </div>
        <div
          className={`tab-item ${activeTab === "visits" ? "active" : ""}`}
          onClick={() => setActiveTab("visits")}
        >
          Visits History
        </div>
        <div
          className={`tab-item ${activeTab === "clinical" ? "active" : ""}`}
          onClick={() => setActiveTab("clinical")}
        >
          Clinical & Medications
        </div>
        <div
          className={`tab-item ${activeTab === "reports" ? "active" : ""}`}
          onClick={() => setActiveTab("reports")}
        >
          Reports & Imaging
        </div>
      </div>

      {activeTab === "overview" && (
        <div className="row g-4 animate-fade-in">
          {/* Left Column */}
          <div className="col-md-4">
            <div className="content-card">
              <div className="card-header-flex">
                <div className="title">General Information</div>
              </div>
              <div className="gi-grid">
                <div className="gi-item">
                  <div className="label">Age</div>
                  <div className="value">
                    {displayAge ? `${displayAge} Years` : "—"}
                  </div>
                </div>
                <div className="gi-item">
                  <div className="label">DOB</div>
                  <div className="value">{formattedDob || "N/A"}</div>
                </div>
                <div className="gi-item">
                  <div className="label">Gender</div>
                  <div className="value">{patientData.gender || "Male"}</div>
                </div>
                <div className="gi-item">
                  <div className="label">UHID</div>
                  <div className="value">{uhid}</div>
                </div>
                <div className="gi-item">
                  <div className="label">ABHA Number</div>
                  <div className="value">{abha || "Not Linked"}</div>
                </div>
                <div className="gi-item">
                  <div className="label">ABHA Address</div>
                  <div className="value">{abhaAddress || "Not Linked"}</div>
                </div>
                <div className="gi-item">
                  <div className="label">Masked Aadhaar</div>
                  <div className="value">
                    {patientData.maskedAadhaar || patientData.aadhaarNumber || patientData.aadhaar_masked || "—"}
                  </div>
                </div>
                <div className="gi-item gi-full mt-2">
                  <div className="label">Current Address</div>
                  <div className="value">
                    {patientData.address ||
                      "123 West Avenue, Brooklyn, New York, 11201"}
                  </div>
                </div>
              </div>
            </div>

            <div className="content-card">
              <div className="card-header-flex">
                <div className="title">Latest Vitals</div>
              </div>
              <div className="vitals-list">
                <div className="vital-row bp">
                  <div className="left">
                    <span className="dot"></span> Blood Pressure
                  </div>
                  <div className="right">
                    {latestVitals?.sbp
                      ? `${latestVitals.sbp}/${latestVitals.dbp} mmHg`
                      : latestVitals?.bloodPressure || "---"}
                  </div>
                </div>
                <div className="vital-row hr">
                  <div className="left">
                    <span className="dot"></span> Heart Rate
                  </div>
                  <div className="right">
                    {latestVitals?.pulse
                      ? `${latestVitals.pulse} bpm`
                      : latestVitals?.pulseRate || "---"}
                  </div>
                </div>
                <div className="vital-row temp">
                  <div className="left">
                    <span className="dot"></span> Temperature
                  </div>
                  <div className="right">
                    {latestVitals?.temp || latestVitals?.temperature || "---"}
                  </div>
                </div>
              </div>
            </div>

            <div className="content-card shadow-none bg-transparent p-0 border-0">
              <div className="card-header-flex px-0">
                <div className="title">Current Medications</div>
                <span className="meta-btn">Active</span>
              </div>
              {prescriptions && prescriptions.length > 0 ? (
                prescriptions.map((p, idx) => (
                  <div className="med-item" key={idx}>
                    <div className="icon-box d-flex align-items-center justify-content-center">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#2563EB" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="m10.5 20.5 10-10a4.95 4.95 0 1 0-7-7l-10 10a4.95 4.95 0 1 0 7 7Z"></path>
                        <path d="m8.5 8.5 7 7"></path>
                      </svg>
                    </div>
                    <div>
                      <div className="name">
                        {p.medicineName || p.name || "Medicine"}
                      </div>
                      <div className="sub">{p.dosage || p.frequency || ""}</div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-secondary small py-2 px-3 bg-white rounded-3 border">
                  No active prescriptions.
                </div>
              )}
            </div>
          </div>

          {/* Middle Column */}
          <div className="col-md-5">
            <div className="content-card">
              <div className="card-header-flex">
                <div className="title">Upcoming Appointments</div>
                <span className="meta-btn">2 Pending</span>
              </div>
              <div className="table-responsive">
                <table className="premium-table">
                  <thead>
                    <tr>
                      <th>Date & Time</th>
                      <th>Department</th>
                      <th>Doctor</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>
                        <div className="fw-bold">Mar 24, 2026</div>
                        <div className="text-secondary small">10:30 AM</div>
                      </td>
                      <td>Cardiology</td>
                      <td>
                        <div className="doc-profile">
                          <img src={UserIcon} alt="dr" />
                          <span>Dr. Michael Chen</span>
                        </div>
                      </td>
                      <td>
                        <span className="status-badge status-confirm">
                          Confirm
                        </span>
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <div className="fw-bold">Mar 02, 2026</div>
                        <div className="text-secondary small">02:15 PM</div>
                      </td>
                      <td>General OPD</td>
                      <td>
                        <div className="doc-profile">
                          <img src={UserIcon} alt="dr" />
                          <span>Dr. Sarah Smith</span>
                        </div>
                      </td>
                      <td>
                        <span className="status-badge status-scheduled">
                          Scheduled
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="content-card">
              <div className="card-header-flex">
                <div className="title">Medical History Summary</div>
                <span
                  className="text-primary fw-bold small cursor-pointer"
                  style={{ fontSize: "12px" }}
                >
                  View Full Record
                </span>
              </div>
              <div className="timeline-wrapper">
                <div className="timeline-node">
                  <div className="date">OCT 12, 2023</div>
                  <div className="title">Hypertension Diagnosis</div>
                  <div className="desc">
                    Patient exhibited high BP readings during routine checkup.
                    Prescribed Lisinopril 10mg.
                  </div>
                </div>
                <div className="timeline-node past">
                  <div className="date">AUG 05, 2023</div>
                  <div className="title">Minor Surgery - Appendectomy</div>
                  <div className="desc">
                    Successful laparoscopic appendectomy. Recovery uneventful.
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column */}
          <div className="col-md-3">
            <div className="content-card shadow-none bg-transparent p-0 border-0">
              <div className="card-header-flex">
                <div className="title">Active Prescriptions</div>
                {/* <button
                  className="btn btn-link text-primary p-0 fw-bold small"
                  style={{ fontSize: "11px", textDecoration: "none" }}
                >
                  Add New
                </button> */}
              </div>
              <div className="rx-card bg-white">
                <div className="rx-top">
                  <span>Hypertension Management</span>
                  <span className="rx-id">RX #44921</span>
                </div>
                <div className="rx-sub">Prescribed: Oct 12, 2023</div>
                <div className="rx-bottom">
                  <span>3 MEDICATIONS</span>
                  <span className="view-link">View Details</span>
                </div>
              </div>
              <div className="rx-card bg-white">
                <div className="rx-top">
                  <span>Diabetes Control Plan</span>
                  <span className="rx-id">RX #43810</span>
                </div>
                <div className="rx-sub">Prescribed: Jul 20, 2023</div>
                <div className="rx-bottom">
                  <span>2 MEDICATIONS</span>
                  <span className="view-link">View Details</span>
                </div>
              </div>
            </div>

            <div className="content-card">
              <div className="card-header-flex">
                <div className="title">Diagnostic Reports</div>
              </div>
              <div className="report-row">
                <div className="info">
                  <div className="icon d-flex align-items-center justify-content-center text-secondary">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"></path>
                      <polyline points="14 2 14 8 20 8"></polyline>
                    </svg>
                  </div>
                  <div>
                    <div className="t">Complete Blood Count</div>
                    <div className="s">Oct 14, 2023 • Lab Dept.</div>
                  </div>
                </div>
                <div className="dl">
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="7 10 12 15 17 10" />
                    <line x1="12" y1="15" x2="12" y2="3" />
                  </svg>
                </div>
              </div>
              <div className="report-row">
                <div className="info">
                  <div className="icon d-flex align-items-center justify-content-center text-secondary">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"></path>
                      <polyline points="14 2 14 8 20 8"></polyline>
                    </svg>
                  </div>
                  <div>
                    <div className="t">Lipid Profile</div>
                    <div className="s">Oct 14, 2023 • Lab Dept.</div>
                  </div>
                </div>
                <div className="dl">
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="7 10 12 15 17 10" />
                    <line x1="12" y1="15" x2="12" y2="3" />
                  </svg>
                </div>
              </div>
            </div>

            <div className="content-card">
              <div className="card-header-flex">
                <div className="title">Imaging Studies</div>
              </div>
              <div className="imaging-study">
                <div className="img-preview shadow-sm">
                  <div className="d-flex h-100 align-items-center justify-content-center bg-dark">
                    <svg
                      width="32"
                      height="32"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#fff"
                      strokeWidth="1"
                      opacity="0.3"
                    >
                      <path d="M12 2v20M2 12h20" />
                      <circle cx="12" cy="12" r="10" />
                    </svg>
                  </div>
                </div>
                <div className="study-top">
                  <span>Chest X-Ray PA View</span>
                  <span className="dicom cursor-pointer fw-bold">
                    View DICOM
                  </span>
                </div>
                <div className="study-date">Aug 05, 2023</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === "visits" && (
        <div className="animate-fade-in">
          {/* Visits History Specific implementation */}
          <div className="d-flex align-items-center gap-3 p-3 mb-4 rounded-3 border bg-white shadow-sm">
            <div style={{ flex: "1" }}>
              <label
                className="text-secondary p-0 m-0 x-small fw-bold text-uppercase mb-1 d-block"
                style={{ fontSize: "10px" }}
              >
                Department
              </label>
              <select className="form-select border-0 bg-light rounded-2 py-1 small fw-medium">
                <option>All Departments</option>
              </select>
            </div>
            <div style={{ flex: "1" }}>
              <label
                className="text-secondary p-0 m-0 x-small fw-bold text-uppercase mb-1 d-block"
                style={{ fontSize: "10px" }}
              >
                Date
              </label>
              <div className="input-group input-group-sm bg-light rounded-2 border-0">
                <span className="input-group-text bg-transparent border-0 d-flex align-items-center">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                    <line x1="16" y1="2" x2="16" y2="6"></line>
                    <line x1="8" y1="2" x2="8" y2="6"></line>
                    <line x1="3" y1="10" x2="21" y2="10"></line>
                  </svg>
                </span>
                <input
                  type="text"
                  className="form-control border-0 bg-transparent py-1 small"
                  placeholder="Select date"
                />
              </div>
            </div>
            <div style={{ flex: "2" }}>
              <label
                className="text-secondary p-0 m-0 x-small fw-bold text-uppercase mb-1 d-block"
                style={{ fontSize: "10px" }}
              >
                Search Records
              </label>
              <div className="input-group input-group-sm bg-light rounded-2 border-0">
                <span className="input-group-text bg-transparent border-0 d-flex align-items-center">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="11" cy="11" r="8"></circle>
                    <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                  </svg>
                </span>
                <input
                  type="text"
                  className="form-control border-0 bg-transparent py-1 small"
                  placeholder="Search by physician, reason or diagnosis..."
                />
              </div>
            </div>
            {/* <div className="mt-auto">
              <button className="btn btn-primary rounded-2 py-1 px-4 shadow-sm fw-bold">
                Search
              </button>
            </div> */}
          </div>

          <div className="content-card p-0 overflow-hidden">
            <table className="premium-table w-100">
              <thead style={{ backgroundColor: "#F8FAFC" }}>
                <tr>
                  <th className="ps-4">Visit No.</th>
                  <th>Date & Time</th>
                  <th>Department</th>
                  <th className="text-center">Visit Type</th>
                  <th>Attending Physician</th>
                  <th>Reason/Diagnosis</th>
                  <th className="text-end pe-4">Action</th>
                </tr>
              </thead>
              <tbody>
                {patientData.visits && patientData.visits.length > 0 ? (
                  patientData.visits.map((v, idx) => (
                    <tr key={idx}>
                      <td className="ps-4 fw-bold">
                        {String(idx + 1).padStart(2, "0")}
                      </td>
                      <td>
                        <div className="fw-bold">
                          {formatDate(v.createdAt || v.visitDate)}
                        </div>
                        <div className="text-secondary small">
                          {formatTime(v.createdAt || v.visitDate)}
                        </div>
                      </td>
                      <td>{v.department || v.departmentId || "General OPD"}</td>
                      <td className="text-center">
                        <span
                          className={`status-badge ${v.visitType === "Emergency" ? "status-scheduled" : "status-confirm"}`}
                        >
                          {v.visitType || "Follow-up"}
                        </span>
                      </td>
                      <td>
                        <div className="doc-profile">
                          <img src={UserIcon} alt="dr" />
                          <span>
                            Dr. {v.doctor || v.doctorName || "Sarah Jenkins"}
                          </span>
                        </div>
                      </td>
                      <td className="text-secondary small">
                        Routine checkup and evaluation
                      </td>
                      <td className="text-end pe-4">
                        <button
                          className="btn btn-link text-primary p-0 fw-bold small text-decoration-none"
                          onClick={() =>
                            navigate(
                              `/visit-summary/${v.visitId || v._id || v.id}`,
                              { state: { patientData } },
                            )
                          }
                        >
                          Summary
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" className="text-center py-5 text-secondary">
                      No records found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {(activeTab === "clinical" || activeTab === "reports") && (
        <div className="content-card p-5 text-center animate-fade-in">
          <div className="mb-3 text-secondary opacity-50 d-flex justify-content-center">
            <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 20h16a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.93a2 2 0 0 1-1.66-.9l-.82-1.2A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13c0 1.1.9 2 2 2Z"></path>
            </svg>
          </div>
          <h5 className="fw-bold">
            {activeTab === "clinical" ? "Clinical Records" : "Lab Reports"}{" "}
            Integration Pending
          </h5>
          <p className="text-secondary small">
            This section is being syncronized with the central medical vault.
          </p>
        </div>
      )}

      <CreateVisitModal
        isOpen={showVisitModal}
        onClose={() => setShowVisitModal(false)}
        patientId={id}
        patientName={displayName}
        patientMeta={
          `${displayAge ? `${displayAge} Years` : ""}${formattedDob ? ` / DOB: ${formattedDob}` : ""} / ${mobile}`
            .replace(/\s*\/\s*\/\s*/g, " / ")
            .trim()
            .replace(/^\s*\/\s*/, "")
            .replace(/\s*\/\s*$/, "")
        }
        onVisitCreated={handleVisitCreated}
      />

      <EditPatientModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        patientData={patientData}
        onUpdateSuccess={handleUpdateSuccess}
      />
    </div>
  );
};

export default PatientDetailNew;
